import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { BRAND, NAV_LINKS } from "../../data/content";
import { CTAButton, scrollToHash, EASE } from "./ui";

const Navbar = () => {
  const [open, setOpen] = useState(false);

  const go = (hash) => (e) => {
    e.preventDefault();
    setOpen(false);
    setTimeout(() => scrollToHash(hash), open ? 350 : 0);
  };

  return (
    <>
      <motion.header
        initial={{ y: -72, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="fixed inset-x-0 top-0 z-50 h-[72px] border-b border-white/[0.08] bg-[#0a0a0a]/[0.88] backdrop-blur-[16px]"
        data-testid="navbar"
      >
        <div className="mx-auto flex h-full max-w-[1440px] items-center justify-between px-6 md:px-12 lg:px-20">
          <a
            href="#top"
            onClick={go("#top")}
            className="flex items-center"
            data-testid="navbar-brand"
            aria-label="INVOGUE — Private & Corporate Concierge"
          >
            <img
              src="/logo-navbar.png"
              alt="INVOGUE — Private & Corporate Concierge"
              className="hidden h-10 w-auto md:block"
            />
            <img
              src="/logo-mark.png"
              alt="INVOGUE"
              className="h-8 w-auto md:hidden"
            />
          </a>

          <nav className="hidden items-center gap-9 lg:flex" aria-label="Navigation principale">
            {NAV_LINKS.map((l) => (
              <a
                key={l.hash}
                href={l.hash}
                onClick={go(l.hash)}
                data-testid={`nav-link-${l.hash.slice(1)}`}
                className="group relative font-sans text-[13px] font-normal text-neutral-400 transition-colors duration-300 hover:text-ivory"
              >
                {l.label}
                <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-champagne transition-all duration-300 group-hover:w-full" />
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-5">
            <span className="hidden font-display text-[10px] font-medium tracking-[0.2em] text-muted sm:block">
              FR
            </span>
            <CTAButton
              onClick={go("#demande")}
              testid="navbar-cta"
              className="max-md:!px-3.5 max-md:!py-2.5 max-md:!text-[10px] md:!px-[22px] md:!py-[14px]"
            >
              Faire une demande
            </CTAButton>
            <button
              className="text-ivory lg:hidden"
              onClick={() => setOpen(true)}
              aria-label="Ouvrir le menu"
              data-testid="navbar-menu-open"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-[60] flex flex-col bg-[#0a0a0a]/[0.98] backdrop-blur-xl lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex h-[72px] items-center justify-between border-b border-white/[0.08] px-6">
              <img src="/logo-navbar.png" alt="INVOGUE" className="h-8 w-auto" />
              <button
                onClick={() => setOpen(false)}
                aria-label="Fermer le menu"
                className="text-ivory"
                data-testid="navbar-menu-close"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <nav className="flex flex-1 flex-col justify-center gap-2 px-8">
              {NAV_LINKS.map((l, i) => (
                <motion.a
                  key={l.hash}
                  href={l.hash}
                  onClick={go(l.hash)}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.5, ease: EASE }}
                  className="border-b border-line py-5 font-serif text-4xl font-medium text-ivory transition-colors hover:text-champagne"
                  data-testid={`mobile-nav-link-${l.hash.slice(1)}`}
                >
                  {l.label}
                </motion.a>
              ))}
            </nav>
            <div className="px-8 pb-10">
              <CTAButton onClick={go("#demande")} testid="mobile-menu-cta" className="w-full">
                Confier une demande
              </CTAButton>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
